/**
 * GlossaryTerm - Inline Term Definition Component
 * 
 * PROPRIETARY NOTICE: THE ONESCENT DIAL
 * © Optimal Mastery LLC and the Onesce family of applied wellness brands.
 * 
 * PURPOSE:
 * Displays CiH glossary terms with inline "?" helpers that show definitions
 * and related FAQs on hover/click. Pulls from centralized glossaryFAQ.js.
 * 
 * THEME: Uses tod-* Tailwind classes for automatic light/dark mode support.
 * RESPONSIVE: Uses useViewportPosition for proper positioning on all devices.
 */

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { HelpCircle, BookOpen, ChevronRight, ExternalLink, X, ArrowRight } from 'lucide-react';
import { getTerm, getRelatedFAQs, GLOSSARY_CATEGORIES } from '../utils/glossaryFAQ';
import { useResponsive } from '../hooks/useResponsive';
import { useViewportPosition, OVERLAY_PRESETS, getTransformOrigin, getAnimationDirection } from '../hooks/useViewportPosition';

// Show/hide delays
const SHOW_DELAY = 300;
const HIDE_DELAY = 200;

/**
 * Main GlossaryTerm component
 */
export const GlossaryTerm = ({
  term,                  // The glossary term key/slug
  children,              // Optional: wrap children with tooltip
  variant = 'text',      // 'text', 'icon', 'highlight'
  showFAQ = true,        // Show related FAQs
  showRelated = true,    // Show related terms with jump-to behavior
  position = 'top',      // Tooltip position
  maxWidth = 280,        // Max width in pixels - reduced for better containment
  onFAQClick,            // Callback when FAQ link is clicked
  onGlossaryClick,       // Callback when "View all" is clicked
  onRelatedTermClick,    // Callback when related term is clicked (close current, jump to new)
  className = '',
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [jumpingToTerm, setJumpingToTerm] = useState(null);
  const triggerRef = useRef(null);
  const showTimeoutRef = useRef(null);
  const hideTimeoutRef = useRef(null);
  
  // Responsive hook for device-aware behavior
  const { isMobile, isPhone, width: viewportWidth, height: viewportHeight, hasTouch } = useResponsive();
  
  // Use viewport position hook for proper positioning
  const { style: positionStyle, actualPosition, isFlipped, isReady } = useViewportPosition(triggerRef, {
    ...OVERLAY_PRESETS.glossary,
    preferredPosition: position,
    maxWidth: isMobile ? Math.min(maxWidth, viewportWidth - 32) : maxWidth,
    maxHeight: isMobile ? Math.min(400, viewportHeight * 0.6) : 400,
  });

  // Get term data
  const termData = getTerm(term);
  const relatedFAQs = showFAQ ? getRelatedFAQs(term) : [];
  const category = termData ? GLOSSARY_CATEGORIES[termData.category] : null;
  
  // Get related terms data
  const relatedTerms = useMemo(() => {
    if (!termData?.relatedTerms?.length) return [];
    return termData.relatedTerms
      .map(slug => getTerm(slug))
      .filter(Boolean)
      .slice(0, isMobile ? 2 : 4); // Show fewer on mobile
  }, [termData, isMobile]);
  
  // Handle related term click - close current, dispatch event to open glossary with term
  const handleRelatedTermClick = useCallback((relatedTerm) => {
    setIsVisible(false);
    setJumpingToTerm(relatedTerm.slug);
    
    // Dispatch custom event for App.js to handle (same pattern as "View in Glossary")
    window.dispatchEvent(new CustomEvent('openGlossary', { 
      detail: { term: relatedTerm.slug, termData: relatedTerm } 
    }));
    
    // Also call callback if provided
    if (onRelatedTermClick) {
      onRelatedTermClick(relatedTerm);
    }
    
    setTimeout(() => setJumpingToTerm(null), 300);
  }, [onRelatedTermClick]);

  // Show handler - now uses useViewportPosition hook for positioning
  const handleShow = useCallback(() => {
    clearTimeout(hideTimeoutRef.current);
    showTimeoutRef.current = setTimeout(() => {
      setIsVisible(true);
    }, hasTouch ? 0 : SHOW_DELAY); // No delay on touch devices
  }, [hasTouch]);

  // Hide handler
  const handleHide = useCallback(() => {
    clearTimeout(showTimeoutRef.current);
    hideTimeoutRef.current = setTimeout(() => {
      setIsVisible(false);
    }, HIDE_DELAY);
  }, []);

  // Cleanup
  useEffect(() => {
    return () => {
      clearTimeout(showTimeoutRef.current);
      clearTimeout(hideTimeoutRef.current);
    };
  }, []);

  // Keep visible on tooltip hover
  const handleTooltipEnter = () => clearTimeout(hideTimeoutRef.current);
  const handleTooltipLeave = () => handleHide();

  // If term not found, just render children or nothing
  if (!termData) {
    return children || null;
  }

  // Render trigger based on variant
  const renderTrigger = () => {
    const triggerProps = {
      ref: triggerRef,
      onMouseEnter: handleShow,
      onMouseLeave: handleHide,
      onFocus: handleShow,
      onBlur: handleHide,
      'data-testid': `glossary-term-${termData.slug}`,
    };

    switch (variant) {
      case 'icon':
        return (
          <button
            {...triggerProps}
            className={`inline-flex items-center justify-center text-tod-text-secondary hover:text-tod-brass transition-colors ${className}`}
            aria-label={`Learn about ${termData.term}`}
          >
            <HelpCircle size={14} />
          </button>
        );

      case 'highlight':
        return (
          <span
            {...triggerProps}
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 bg-tod-brass/10 border border-tod-brass/20 rounded text-tod-text-primary cursor-help ${className}`}
          >
            {children || termData.term}
            <HelpCircle size={12} className="text-tod-brass/60" />
          </span>
        );

      default: // 'text'
        // IMPORTANT: Use 'inherit' for text color so terms match their context
        // Phase-colored text stays phase-colored, neutral text stays neutral
        // Only the underline and icon use brass for visual differentiation
        return (
          <span
            {...triggerProps}
            className={`inline-flex items-center gap-0.5 border-b border-dotted border-tod-text-muted/60 cursor-help hover:border-tod-text-secondary transition-colors ${className}`}
            style={{ color: 'inherit' }}
          >
            {children || termData.term}
            <HelpCircle size={10} className="text-tod-text-secondary ml-0.5" />
          </span>
        );
    }
  };

  // Get position styles - now from useViewportPosition hook
  const getPositionStyles = () => {
    if (!isReady) {
      return { position: 'fixed', opacity: 0, visibility: 'hidden' };
    }
    return {
      ...positionStyle,
      zIndex: 10010, // Higher than tutorial overlay (z-10000) so tooltips appear on top
      transform: actualPosition === 'top' || actualPosition === 'top-left' || actualPosition === 'top-right'
        ? 'translateY(-100%)' 
        : 'translateY(0)',
    };
  };
  
  // Get animation direction based on position
  const animationDir = getAnimationDirection(actualPosition, isFlipped);

  return (
    <>
      {renderTrigger()}

      {createPortal(
        <AnimatePresence>
          {isVisible && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95, ...animationDir }}
              animate={{ opacity: 1, scale: 1, y: 0, x: 0 }}
              exit={{ opacity: 0, scale: 0.95, ...animationDir }}
              transition={{ duration: 0.15, ease: 'easeOut' }}
              style={{ ...getPositionStyles(), transformOrigin: getTransformOrigin(actualPosition) }}
              className="pointer-events-auto"
              onMouseEnter={handleTooltipEnter}
              onMouseLeave={handleTooltipLeave}
              onClick={isMobile ? (e) => e.stopPropagation() : undefined}
              data-testid="glossary-tooltip"
            >
              <div 
                className="bg-tod-bg-primary border-2 border-tod-brass/50 rounded-lg shadow-2xl shadow-black/60 overflow-hidden overflow-y-auto"
                style={{
                  width: isMobile ? Math.min(maxWidth, viewportWidth - 32) : maxWidth,
                  maxWidth: isMobile ? Math.min(maxWidth, viewportWidth - 32) : maxWidth,
                  maxHeight: isMobile ? 'calc(100vh - 120px)' : 'calc(100vh - 200px)',
                }}
              >
                {/* Header */}
                <div 
                  className="px-4 py-3 border-b border-tod-brass/30"
                  style={{ backgroundColor: category?.color + '15' }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <BookOpen size={16} style={{ color: category?.color }} />
                      <span className="text-sm font-bold text-tod-text-primary">
                        {termData.term}
                      </span>
                    </div>
                    {category && (
                      <span 
                        className="text-[10px] px-2 py-1 rounded uppercase tracking-wider font-medium"
                        style={{ 
                          backgroundColor: category.color + '25',
                          color: category.color
                        }}
                      >
                        {category.label}
                      </span>
                    )}
                  </div>
                </div>

                {/* Definition */}
                <div className="px-4 py-3">
                  <p className="text-sm text-tod-text-secondary leading-relaxed">
                    {termData.definition}
                  </p>

                  {/* Aliases */}
                  {termData.aliases?.length > 0 && (
                    <p className="text-xs text-tod-text-secondary mt-2">
                      Also: {termData.aliases.join(', ')}
                    </p>
                  )}

                  {/* Constraints/Notes */}
                  {termData.constraints?.length > 0 && (
                    <div className="mt-3 p-3 bg-tod-warning/15 border border-tod-warning/30 rounded">
                      <p className="text-xs text-tod-warning font-medium">
                        ⚠ {termData.constraints[0]}
                      </p>
                    </div>
                  )}
                </div>

                {/* Related FAQs */}
                {showFAQ && relatedFAQs.length > 0 && (
                  <div className="px-4 py-3 border-t border-tod-brass/20 bg-tod-bg-secondary/60">
                    <p className="text-xs text-tod-text-secondary mb-2 font-medium">Related Questions:</p>
                    <div className="space-y-1.5">
                      {relatedFAQs.slice(0, 2).map(faq => (
                        <button
                          key={faq.id}
                          onClick={() => {
                            // Dispatch event to open FAQ in glossary panel
                            window.dispatchEvent(new CustomEvent('openFAQ', { 
                              detail: { faq } 
                            }));
                            onFAQClick?.(faq);
                          }}
                          className="w-full text-left text-xs text-tod-brass hover:text-tod-text-primary flex items-center gap-1.5 group"
                        >
                          <ChevronRight size={12} className="opacity-50 group-hover:opacity-100" />
                          <span className="truncate">{faq.question}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Related Terms with Close Current + Jump To behavior */}
                {showRelated && relatedTerms.length > 0 && (
                  <div className="px-4 py-3 border-t border-tod-brass/20 bg-tod-bg-secondary/50">
                    <p className="text-xs text-tod-text-secondary mb-2 font-medium">Related Terms:</p>
                    <div className="flex flex-wrap gap-1.5">
                      {relatedTerms.map(relatedTerm => (
                        <button
                          key={relatedTerm.slug}
                          onClick={() => handleRelatedTermClick(relatedTerm)}
                          className={`
                            px-2 py-1 text-[11px] rounded 
                            bg-tod-brass/15 border border-tod-brass/30
                            text-tod-brass hover:text-tod-text-primary hover:bg-tod-brass/25
                            transition-all duration-150
                            flex items-center gap-1 group font-medium
                            ${jumpingToTerm === relatedTerm.slug ? 'ring-2 ring-tod-gold' : ''}
                          `}
                          title={`Close current & jump to "${relatedTerm.term}"`}
                          data-testid={`related-term-${relatedTerm.slug}`}
                        >
                          <span>{relatedTerm.term}</span>
                          <ArrowRight size={10} className="opacity-50 group-hover:opacity-100 group-hover:translate-x-0.5 transition-transform" />
                        </button>
                      ))}
                    </div>
                    <p className="text-[10px] text-tod-text-secondary mt-2 italic">
                      Click to close & jump to term
                    </p>
                  </div>
                )}

                {/* Footer - View all link */}
                <div className="px-4 py-2 border-t border-tod-brass/20 bg-tod-bg-secondary/40">
                  <button
                    onClick={() => {
                      // Close tooltip first
                      setIsVisible(false);
                      // Dispatch custom event for App.js to handle
                      window.dispatchEvent(new CustomEvent('openGlossary', { 
                        detail: { term: termData?.slug || term, termData } 
                      }));
                      // Also call callback if provided
                      onGlossaryClick?.(termData);
                    }}
                    className="text-xs text-tod-text-secondary hover:text-tod-brass flex items-center gap-1.5 font-medium"
                  >
                    <span>View in Glossary</span>
                    <ExternalLink size={12} />
                  </button>
                </div>

                {/* Arrow */}
                <div 
                  className={`absolute w-3 h-3 bg-tod-bg-primary border-tod-brass/50 rotate-45 ${
                    position === 'top' 
                      ? 'bottom-[-7px] left-1/2 -translate-x-1/2 border-r border-b' 
                      : 'top-[-7px] left-1/2 -translate-x-1/2 border-l border-t'
                  }`}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
};

/**
 * GlossaryIcon - Just an info icon that shows term definition
 */
export const GlossaryIcon = ({
  term,
  position = 'top',
  className = '',
}) => (
  <GlossaryTerm
    term={term}
    variant="icon"
    position={position}
    className={className}
  />
);

/**
 * HighlightedTerm - Term with background highlight
 */
export const HighlightedTerm = ({
  term,
  children,
  position = 'top',
}) => (
  <GlossaryTerm
    term={term}
    variant="highlight"
    position={position}
  >
    {children}
  </GlossaryTerm>
);

/**
 * FAQItem - Expandable FAQ question/answer
 */
export const FAQItem = ({
  faq,
  isExpanded = false,
  onToggle,
  className = '',
}) => {
  const [expanded, setExpanded] = useState(isExpanded);
  
  const handleToggle = () => {
    setExpanded(!expanded);
    onToggle?.(!expanded);
  };

  return (
    <div 
      className={`border border-tod-brass/20 rounded overflow-hidden ${className}`}
      data-testid={`faq-item-${faq.id}`}
    >
      <button
        onClick={handleToggle}
        className="w-full px-4 py-3 text-left flex items-center justify-between gap-3 bg-tod-bg-primary/50 hover:bg-tod-brass/5 transition-colors"
      >
        <span className="text-sm text-tod-text-primary font-medium">{faq.question}</span>
        <ChevronRight 
          size={16} 
          className={`text-tod-brass transition-transform ${expanded ? 'rotate-90' : ''}`} 
        />
      </button>
      
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className="px-4 py-3 border-t border-tod-brass/10 bg-tod-bg-secondary/30">
              <p className="text-sm text-tod-text-secondary leading-relaxed">{faq.answer}</p>
              
              {/* Related terms */}
              {faq.relatedTerms?.length > 0 && (
                <div className="mt-3 pt-2 border-t border-tod-brass/10">
                  <span className="text-[10px] text-tod-text-secondary">Related: </span>
                  {faq.relatedTerms.map((term, idx) => (
                    <GlossaryTerm key={term} term={term} variant="text" className="text-xs">
                      {term}
                      {idx < faq.relatedTerms.length - 1 && <span className="text-tod-text-secondary">, </span>}
                    </GlossaryTerm>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Default export
export default GlossaryTerm;
